import { HStack, VStack, Button, IconButton, Text } from '@cad-arc';
import { useFieldArray, UseFormReturn, useWatch } from 'react-hook-form';
import { FormNumberInput } from '../forms/integrated-form-controls/form-number-input';
import { FormSection } from '../search-windows/form-section';
import { SearchLocationForm } from './types';
import { validateLatitude, validateLongitude } from '../search-windows/utils';
import { isValidLatLng } from '../location/helpers';
import { LocationPolygonSearchMap } from '../search-windows/location-polygon-search-map';
import { LocationSearchMapPlaceholder } from '../search-windows/location-search-map-placeholder';

export function SearchFormPolygonSearchSection({
    form,
}: {
    form: UseFormReturn<SearchLocationForm>;
}) {
    const {
        fields: polygonPointFields,
        append: appendPolygonPoint,
        remove: removePolygonPoint,
    } = useFieldArray({
        control: form.control,
        name: 'polygonPoints',
    });
    const polygonPoints = useWatch({ control: form.control, name: 'polygonPoints' });
    const validPoints = polygonPoints
        .filter((point) => isValidLatLng(point.latitude, point.longitude))
        .map((point) => ({
            latitude: parseFloat(point.latitude!),
            longitude: parseFloat(point.longitude!),
        }));
    const isPolygonPointFormValid = validPoints.length >= 3;
    return (
        <HStack
            data-testid="polygon-search-section"
            w="100%"
            justifyContent="space-between"
            alignItems="flex-start"
        >
            <VStack align="left">
                <Text variant="headingXs">Polygon Search</Text>
                <FormSection>
                    {polygonPointFields.map((_, index) => {
                        return (
                            <HStack key={index} spacing={4}>
                                <HStack align="flex-start" w="360px" spacing={4}>
                                    <FormNumberInput
                                        name={`polygonPoints.${index}.latitude`}
                                        label="Latitude"
                                        control={form.control}
                                        length="md"
                                        rules={{
                                            validate: validateLatitude,
                                        }}
                                    />
                                    <FormNumberInput
                                        name={`polygonPoints.${index}.longitude`}
                                        label="Longitude"
                                        control={form.control}
                                        length="md"
                                        rules={{
                                            validate: validateLongitude,
                                        }}
                                    />
                                </HStack>
                                <HStack alignSelf="flex-end" spacing={4}>
                                    {polygonPoints.length > 3 && (
                                        <IconButton
                                            icon="TrashCan"
                                            variant="ghost"
                                            aria-label="remove lat/long"
                                            onClick={() => removePolygonPoint(index)}
                                        />
                                    )}
                                    {index === polygonPoints.length - 1 && (
                                        <Button
                                            variant="outline"
                                            leadingVisual="Add"
                                            onClick={() =>
                                                appendPolygonPoint({
                                                    latitude: '',
                                                    longitude: '',
                                                })
                                            }
                                        >
                                            Additional Lat/Long
                                        </Button>
                                    )}
                                </HStack>
                            </HStack>
                        );
                    })}
                </FormSection>
            </VStack>
            {isPolygonPointFormValid ? (
                <LocationPolygonSearchMap points={validPoints} />
            ) : (
                <LocationSearchMapPlaceholder />
            )}
        </HStack>
    );
}
